import { PortfolioFee, Portfolio } from '@models/fees/fee.models';
import { FeesRepo } from "@services/fees.repo";
import { AuthService } from "@common/auth.service";
import { Roles } from "@common/enums/Roles";
import * as moment from "moment";
import * as _ from "lodash";
import angular = require("angular");
import { extractErrorMessage } from "@common/utils/errorUtils";

export class FeeCardsController {
  public clientPortfolioFees: PortfolioFee[] = [];
  public client: any; // Full client (clientDetails) with clientNumber and portfolios
  public onRefresh: () => void; // Callback so parent can refresh client details
  public canEdit: boolean = true; // Default to true for backward compatibility

  public loadingFees: Set<string> = new Set(); // Track which fees are being processed
  public isLoading: boolean = false; // Track if fees are currently being loaded

  /** Senior Management or System Administrator — required for portfolio fee create/edit (in addition to parent `canEdit`). */
  public hasPortfolioFeeManageRole = (): boolean => {
    const userRoles = this.authService.getUserRoles();
    if (!userRoles) return false;
    return (
      userRoles.includes(Roles.SeniorManagement) ||
      userRoles.includes(Roles.SystemAdministrator)
    );
  };

  /** Shared permission for add and edit portfolio fees. */
  public get canManagePortfolioFees(): boolean {
    return !!(this.canEdit && this.hasPortfolioFeeManageRole());
  }

  getStaleReasonsTitle(clientFee: any): string {
    if (!clientFee || !clientFee.staleReasons || !clientFee.staleReasons.length) {
      return "";
    }
    return clientFee.staleReasons.join("; ");
  }

  /** Can add a new portfolio fee. */
  public get canAddFee(): boolean {
    return this.canManagePortfolioFees;
  }

  /** Can open the edit dialog for an existing portfolio fee. */
  public get canEditPortfolioFee(): boolean {
    return this.canManagePortfolioFees;
  }

  public getPortfolioFeeAddButtonTitle = (): string => {
    if (this.canAddFee) {
      return "Add Portfolio Fee";
    }
    if (!this.canEdit) {
      return "Read only";
    }
    return "Only a senior manager or system administrator can add a portfolio fee";
  };

  public getPortfolioFeeEditButtonTitle = (): string => {
    if (this.canEditPortfolioFee) {
      return "Edit Fee";
    }
    if (!this.canEdit) {
      return "Read only";
    }
    return "Only a senior manager or system administrator can edit portfolio fees";
  };

  /** @ngInject */
  constructor(
    private notifyjs: any,
    private feesRepo: FeesRepo,
    private authService: AuthService,
    private $mdDialog: angular.material.IDialogService,
    private $rootScope: angular.IRootScopeService
  ) {}


  $onChanges = (changes: any) => {
    if (changes.client) {
      if (!changes.client.currentValue || !changes.client.currentValue.clientNumber) {
        this.clientPortfolioFees = [];
        this.isLoading = false;
        return;
      }
      if (changes.client.currentValue && changes.client.currentValue.clientNumber) {
        this.getClientFees();
      }
    }
  };

  getClientFees = () => {
    if (!this.client || !this.client.clientNumber) {
      return;
    }

    // Clear previous fees immediately to show loading state
    this.clientPortfolioFees = [];
    this.isLoading = true;
    this.feesRepo
      .getClientFees(this.client.clientNumber)
      .then((res) => {
        this.clientPortfolioFees = res.data as Array<any>;
        if (this.clientPortfolioFees.length === 0) {
          return;
        }
        this.clientPortfolioFees.forEach((fee) => {
          if (fee.feeType && fee.feeType.feeTiers) {
            fee.feeType.feeTiers.sort((a: any, b: any) => {
              return a.low > b.low ? 1 : -1;
            });
          }
        });
      })
      .catch((err) => {
        this.notifyjs("Failed to load client fees : " + extractErrorMessage(err));
      })
      .finally(() => {
        this.isLoading = false;
      });
  };

  handleRefresh = () => {
    this.getClientFees();
    if (this.onRefresh) {
      this.onRefresh();
    }
  };

  public convertToLocaleTime = (date: any): string => {
    if (!date) return '';
    return moment(new Date(date)).format("YYYY-MM-DD");
  };

  public formatBooleanValue = (value: boolean): string => {
    return value ? "Yes" : "No";
  };

  public convertDecToPerc = (value: number): number => {
    return +(value * 100).toFixed(4);
  };

  public isPortfolioInClientFee = (clientFee: PortfolioFee, portfolio: Portfolio): boolean => {
    return clientFee.portfolios.some((p) => p.portfolioId === portfolio.portfolioId);
  };

  public togglePortfolioInClientFee = (clientFee: PortfolioFee, portfolio: Portfolio): void => {
    const existingIndex = clientFee.portfolios.findIndex((p) => p.portfolioId === portfolio.portfolioId);

    if (existingIndex > -1) {
      // Portfolio exists, check if it's the last one
      if (clientFee.portfolios.length <= 1) {
        // Prevent unchecking the last portfolio
        this.notifyjs('At least one portfolio must be selected');
        return;
      }
      // Portfolio exists and there are others, remove it
      clientFee.portfolios.splice(existingIndex, 1);
    } else {
      // Portfolio doesn't exist, add it
      clientFee.portfolios.push(portfolio);
    }
  };

  public isPortfolioInOtherClientFee = (currentClientFee: PortfolioFee, portfolio: Portfolio): boolean => {
    return this.clientPortfolioFees.some((otherClientFee) => {
      // Skip the current client fee we're checking
      if (otherClientFee === currentClientFee) {
        return false;
      }
      // Check if portfolio exists in this other client fee
      return otherClientFee.portfolios.some((p) => p.portfolioId === portfolio.portfolioId);
    });
  };

  public isLastSelectedPortfolio = (clientFee: PortfolioFee, portfolio: Portfolio): boolean => {
    return clientFee.portfolios.length === 1 && this.isPortfolioInClientFee(clientFee, portfolio);
  };

  public getAvailablePortfoliosForFee = (currentClientFee: PortfolioFee): Portfolio[] => {
    const portfolios = this.client && this.client.portfolios;
    if (!portfolios) {
      return [];
    }

    return portfolios.filter((portfolio: Portfolio) => {
      return !this.isPortfolioInOtherClientFee(currentClientFee, portfolio);
    });
  };


  public openAddDialog = (): void => {
    if (!this.canManagePortfolioFees) return;

    const tempScope = this.$rootScope.$new(true) as any;
    tempScope.portfolioFee = null; // Add mode
    const portfolios = (this.client && this.client.portfolios) || [];
    tempScope.clientPortfolios = portfolios;
    tempScope.potentialPortfoliosForFees = portfolios;
    tempScope.clientPortfolioFees = this.clientPortfolioFees || [];

    this.$mdDialog
      .show({
        template: `<md-dialog flex="50"><create-portfolio-fee-dialog portfolio-fee="portfolioFee"
                  client-portfolios="clientPortfolios" potential-portfolios-for-fees="potentialPortfoliosForFees"
                  client-portfolio-fees="clientPortfolioFees">
                  </create-portfolio-fee-dialog></md-dialog>`,
        clickOutsideToClose: true,
        scope: tempScope,
      })
      .then((result) => {
        if (result) {
          this.handleRefresh();
        }
      })
      .catch(() => {
        // Dialog was cancelled, do nothing
      });
  };

  public openEditDialog = (clientFee: PortfolioFee): void => {
    if (!this.canManagePortfolioFees) return;
    
    // Open dialog immediately - fee types will be loaded inside the dialog
    const tempScope = this.$rootScope.$new(true) as any;
    
    // Create a deep copy of the portfolio fee to avoid reference issues
    const portfolioFeeForEdit = JSON.parse(JSON.stringify(clientFee));
    
    // Ensure all properties are set
    portfolioFeeForEdit.id = clientFee.id;
    portfolioFeeForEdit.feeType = clientFee.feeType;
    portfolioFeeForEdit.value = clientFee.value;
    portfolioFeeForEdit.portfolios = clientFee.portfolios || [];
    portfolioFeeForEdit.fromDate = clientFee.fromDate;
    portfolioFeeForEdit.toDate = clientFee.toDate;
    portfolioFeeForEdit.comment = clientFee.comment || '';
    portfolioFeeForEdit.approved = clientFee.approved;
    // Copy setupFees array (new structure supports multiple setup fees)
    portfolioFeeForEdit.setupFees = clientFee.setupFees || [];
    // Copy maker-checker flags for approval validation (creator and last editor cannot approve)
    portfolioFeeForEdit.isAddedByCurrentUser = (clientFee as any).isAddedByCurrentUser;
    portfolioFeeForEdit.isLastEditedByCurrentUser = (clientFee as any).isLastEditedByCurrentUser;
    
    tempScope.portfolioFee = portfolioFeeForEdit;
    // Don't pass feeTypes - let the dialog load them internally
    const portfolios = (this.client && this.client.portfolios) || [];
    tempScope.clientPortfolios = portfolios;
    tempScope.potentialPortfoliosForFees = portfolios;
    tempScope.clientPortfolioFees = this.clientPortfolioFees || [];

    this.$mdDialog
      .show({
        template: `<md-dialog flex="50"><create-portfolio-fee-dialog portfolio-fee="portfolioFee"
                  client-portfolios="clientPortfolios" potential-portfolios-for-fees="potentialPortfoliosForFees"
                  client-portfolio-fees="clientPortfolioFees">
                  </create-portfolio-fee-dialog></md-dialog>`,
        clickOutsideToClose: true,
        scope: tempScope,
      })
      .then((result) => {
        if (result) {
          this.handleRefresh();
        }
      })
      .catch(() => {
        // Dialog was cancelled, do nothing
      });
  };

  public getAddedByName = (clientFee: PortfolioFee): string => {
    if (!clientFee.addedBy) return '';
    // Handle both cases: addedBy as string (name) or as User object
    return typeof clientFee.addedBy === 'string' 
      ? clientFee.addedBy 
      : (clientFee.addedBy as any).name || '';
  };

  public isLoadingFee = (clientFee: PortfolioFee): boolean => {
    return this.loadingFees.has(clientFee.id.toString());
  };
}

export const FeeCardsComponent = {
  template: require('./feeCards.html'),
  style: require('./feeCards.scss'),
  controller: FeeCardsController,
  controllerAs: '$ctrl',
  bindings: {
    client: '<', // Full client (clientDetails) with clientNumber and portfolios
    onRefresh: '&', // Called so parent can refresh client details (e.g. after edit)
    canEdit: '<' // Whether user can edit fees
  }
};




// WEBPACK FOOTER //
// ./src/app/fees/feeCards/feeCards.ts